import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { CountryLabel } from '../../../shared/components/CountryFlag';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Card,
    Drawer,
    Popconfirm,
    Tooltip,
    InputNumber,
    Typography,
    Empty,
    Tabs,
    Alert
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    CloudOutlined,
    SyncOutlined,
    ApartmentOutlined,
    ApiOutlined
} from '@ant-design/icons';
import evmService from '../../../shared/services/evmService';
import apiClient from '../../../shared/services/apiClient';
import { VpcPeeringTab, VpcTrafficTab } from './VpcPeeringPanel';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const MyVpc = () => {
    const { t } = useTranslation();
    const [vpcs, setVpcs] = useState([]);
    const [areas, setAreas] = useState([]);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
    const [editDrawerVisible, setEditDrawerVisible] = useState(false);
    const [subnetModalVisible, setSubnetModalVisible] = useState(false);
    const [currentVpc, setCurrentVpc] = useState(null);
    const [incoming, setIncoming] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [rowTab, setRowTab] = useState({});

    const openPeering = (record) => {
        setRowTab((s) => ({ ...s, [record.id]: 'peering' }));
        setExpandedKeys((keys) => (keys.includes(record.id) ? keys : [...keys, record.id]));
    };

    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const [subnetForm] = Form.useForm();

    useEffect(() => {
        loadVpcs();
        loadAreas();
        loadIncoming();
    }, []);

    // Peering requests waiting for this user's answer.
    const loadIncoming = async () => {
        try {
            const res = await apiClient.get('/api/user/evm/vpc-peering');
            const rows = res.data?.data?.peerings || [];
            setIncoming(rows.filter((p) => p.status === 'pending' && p.role === 'accepter'));
        } catch (e) {
            setIncoming([]);
        }
    };

    const loadVpcs = async () => {
        setLoading(true);
        try {
            const response = await evmService.getVpcList();
            if (response.status === 'success') {
                setVpcs(response.data?.vpcs || []);
            } else {
                message.error(response.msg || t('Failed to load VPC'));
            }
        } catch (error) {
            message.error(t('Failed to load VPC'));
        } finally {
            setLoading(false);
        }
    };

    const loadAreas = async () => {
        try {
            const response = await evmService.getVpcAreas();
            if (response.status === 'success') {
                setAreas(response.data || []);
            }
        } catch (error) {
            console.error('Failed to load areas', error);
        }
    };

    const loadZonesByArea = async (areaId) => {
        try {
            const response = await evmService.getVpcZones(areaId);
            if (response.status === 'success') {
                setZones(response.data?.zones || []);
            }
        } catch (error) {
            console.error('Failed to load zones', error);
        }
    };

    const showCreateDrawer = () => {
        createForm.resetFields();
        setZones([]);
        setCreateDrawerVisible(true);
    };

    const showEditDrawer = (vpc) => {
        setCurrentVpc(vpc);
        editForm.setFieldsValue(vpc);
        setEditDrawerVisible(true);
    };

    const showSubnetModal = (vpc) => {
        setCurrentVpc(vpc);
        subnetForm.resetFields();
        setSubnetModalVisible(true);
    };

    const handleCreate = async (values) => {
        try {
            const response = await evmService.createVpc(values);
            if (response.status === 'success') {
                message.success(t('VPC created'));
                setCreateDrawerVisible(false);
                loadVpcs();
            } else {
                message.error(response.msg || t('Failed to create VPC'));
            }
        } catch (error) {
            message.error(t('Failed to create VPC'));
        }
    };

    const handleUpdate = async (values) => {
        try {
            const response = await evmService.updateVpc(currentVpc.id, values);
            if (response.status === 'success') {
                message.success(t('VPC updated'));
                setEditDrawerVisible(false);
                loadVpcs();
            } else {
                message.error(response.msg || t('Failed to update VPC'));
            }
        } catch (error) {
            message.error(t('Failed to update VPC'));
        }
    };

    const handleDelete = async (vpc) => {
        try {
            const response = await evmService.deleteVpc(vpc.id);
            if (response.status === 'success') {
                message.success(t('VPC deleted'));
                loadVpcs();
            } else {
                message.error(response.msg || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    const handleAddSubnet = async (values) => {
        try {
            const response = await evmService.addVpcSubnet(currentVpc.id, values);
            if (response.status === 'success') {
                message.success(t('Subnet added'));
                setSubnetModalVisible(false);
                loadVpcs();
            } else {
                message.error(response.msg || t('Failed to add subnet'));
            }
        } catch (error) {
            message.error(t('Failed to add subnet'));
        }
    };

    const handleDeleteSubnet = async (vpcId, subnetId) => {
        try {
            const response = await evmService.deleteVpcSubnet(vpcId, subnetId);
            if (response.status === 'success') {
                message.success(t('Subnet deleted'));
                loadVpcs();
            } else {
                message.error(response.msg || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    const getStatusTag = (status) => {
        const statusMap = {
            'active': { color: 'green', text: t('Normal') },
            'inactive': { color: 'red', text: t('Disabled') },
            'pending': { color: 'orange', text: t('Creating') }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const expandedRowRender = (record) => (
        <Tabs
            size="small"
            activeKey={rowTab[record.id] || 'subnets'}
            onChange={(k) => setRowTab((s) => ({ ...s, [record.id]: k }))}
            items={[
                { key: 'subnets', label: t('Subnet'), children: subnetTable(record) },
                { key: 'peering', label: t('Peering'), children: <VpcPeeringTab vpc={record} onChanged={loadIncoming} /> },
                { key: 'traffic', label: t('Cross-zone traffic'), children: <VpcTrafficTab vpc={record} /> },
            ]}
        />
    );

    const subnetTable = (record) => {
        const subnets = record.subnets || [];
        if (subnets.length === 0) {
            return <Empty description={t('No subnets yet')} />;
        }

        const subnetColumns = [
            { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
            { title: t('Name'), dataIndex: 'name', key: 'name', width: 150 },
            { title: 'CIDR', dataIndex: 'cidr', key: 'cidr', width: 150 },
            { title: t('Gateway'), dataIndex: 'gateway', key: 'gateway', width: 140 },
            {
                title: '',
                key: 'action',
                width: 60,
                render: (_, subnet) => (
                    <Popconfirm
                        title={t('Delete this subnet?')}
                        onConfirm={() => handleDeleteSubnet(record.id, subnet.id)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                )
            }
        ];

        return (
            <Table
                columns={subnetColumns}
                dataSource={subnets}
                pagination={false}
                size="small"
                rowKey="id"
            />
        );
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            width: 150
        },
        {
            title: 'CIDR',
            dataIndex: 'cidr',
            key: 'cidr',
            width: 140
        },
        {
            title: 'VNI',
            dataIndex: 'vni',
            key: 'vni',
            width: 100
        },
        {
            title: t('Availability zone'),
            dataIndex: 'zone_name',
            key: 'zone_name',
            width: 120,
            render: (name, record) => name || t('Availability zone {{zone_id}}', { zone_id: record.zone_id })
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => getStatusTag(status)
        },
        {
            title: t('Cloud server count'),
            dataIndex: 'vm_count',
            key: 'vm_count',
            width: 80,
            render: (count) => count || 0
        },
        {
            title: t('Actions'),
            key: 'action',
            fixed: 'right',
            width: 190,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title={t('Add subnet')}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => showSubnetModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('Peering')}>
                        <Button
                            size="small"
                            icon={<ApiOutlined />}
                            onClick={() => openPeering(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('Edit')}>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => showEditDrawer(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={t('Delete this VPC?')}
                        description={t('All subnets within it will also be deleted.')}
                        onConfirm={() => handleDelete(record)}
                        disabled={record.vm_count > 0}
                    >
                        <Tooltip title={record.vm_count > 0 ? t('This VPC still contains cloud servers and cannot be deleted') : t('Delete')}>
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={record.vm_count > 0}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div>
            <Card
                title={<span><ApartmentOutlined /> {t('VPC')}</span>}
                extra={
                    <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={showCreateDrawer}>
                            {t('Create VPC')}
                        </Button>
                        <Button icon={<SyncOutlined />} onClick={loadVpcs}>
                            {t('Refresh')}
                        </Button>
                    </Space>
                }
            >
                {incoming.length > 0 && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message={t('{{n}} peering request(s) pending: VPC {{ids}}', {
                            n: incoming.length,
                            ids: [...new Set(incoming.map((p) => p.accepter.vpc_id))].join(', '),
                        })}
                    />
                )}
                <Table
                    columns={columns}
                    dataSource={vpcs}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 10 }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={t('No VPC yet. Click "Create VPC" in the top right.')}
                            />
                        )
                    }}
                    expandable={{
                        expandedRowRender,
                        rowExpandable: (record) => true,
                        expandedRowKeys: expandedKeys,
                        onExpandedRowsChange: (keys) => setExpandedKeys(keys)
                    }}
                />
            </Card>

            {/* Create VPC Drawer */}
            <Drawer
                title={t('Create VPC')}
                width={450}
                open={createDrawerVisible}
                onClose={() => setCreateDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setCreateDrawerVisible(false)}>{t('Cancel')}</Button>
                        <Button type="primary" onClick={() => createForm.submit()}>{t('Create')}</Button>
                    </Space>
                }
            >
                <Form form={createForm} layout="vertical" onFinish={handleCreate}>
                    <Form.Item
                        name="name"
                        label={t('VPC name')}
                        rules={[{ required: true, message: t('Please enter a VPC name') }]}
                    >
                        <Input placeholder={t('E.g. my-vpc')} />
                    </Form.Item>

                    <Form.Item
                        name="cidr"
                        label={t('CIDR range')}
                        rules={[{ required: true, message: t('Please enter a CIDR') }]}
                        tooltip={t('E.g. 10.0.0.0/16')}
                    >
                        <Input placeholder="10.0.0.0/16" />
                    </Form.Item>

                    <Form.Item name="area_id" label={t('Region')}>
                        <Select
                            placeholder={t('Select a region')}
                            onChange={(value) => loadZonesByArea(value)}
                            allowClear
                        >
                            {areas.map(area => (
                                <Option key={area.id} value={area.id}><CountryLabel code={area.code || area.remark}>{area.name}</CountryLabel></Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="zone_id"
                        label={t('Availability zone')}
                        rules={[{ required: true, message: t('Select an availability zone') }]}
                    >
                        <Select placeholder={t('Select an availability zone')}>
                            {zones.map(zone => (
                                <Option key={zone.id} value={zone.id}>{zone.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="mtu"
                        label="MTU"
                        tooltip={t('Maximum transmission unit (1280-9000), default 1500')}
                        initialValue={1500}
                    >
                        <InputNumber
                            min={1280}
                            max={9000}
                            style={{ width: '100%' }}
                            placeholder="1500"
                        />
                    </Form.Item>

                    <Form.Item name="description" label={t('Description')}>
                        <TextArea rows={3} placeholder={t('Optional description')} />
                    </Form.Item>
                </Form>
            </Drawer>

            {/* Edit VPC Drawer */}
            <Drawer
                title={t('Edit VPC')}
                width={450}
                open={editDrawerVisible}
                onClose={() => setEditDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setEditDrawerVisible(false)}>{t('Cancel')}</Button>
                        <Button type="primary" onClick={() => editForm.submit()}>{t('Save')}</Button>
                    </Space>
                }
            >
                <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
                    <Form.Item
                        name="name"
                        label={t('VPC name')}
                        rules={[{ required: true, message: t('Please enter a VPC name') }]}
                    >
                        <Input placeholder={t('VPC name')} />
                    </Form.Item>

                    <Form.Item
                        name="mtu"
                        label="MTU"
                        tooltip={t('Maximum transmission unit (1280-9000)')}
                    >
                        <InputNumber
                            min={1280}
                            max={9000}
                            style={{ width: '100%' }}
                            placeholder="1500"
                        />
                    </Form.Item>

                    <Form.Item name="description" label={t('Description')}>
                        <TextArea rows={3} placeholder={t('Optional description')} />
                    </Form.Item>
                </Form>
            </Drawer>

            {/* Add Subnet Modal */}
            <Modal
                title={t('Add subnet to {{value}}', { value: currentVpc?.name || '' })}
                open={subnetModalVisible}
                onCancel={() => setSubnetModalVisible(false)}
                onOk={() => subnetForm.submit()}
            >
                <Form form={subnetForm} layout="vertical" onFinish={handleAddSubnet}>
                    <Form.Item
                        name="name"
                        label={t('Subnet name')}
                        rules={[{ required: true, message: t('Please enter a subnet name') }]}
                    >
                        <Input placeholder={t('E.g. subnet-1')} />
                    </Form.Item>

                    <Form.Item
                        name="cidr"
                        label={t('CIDR range')}
                        rules={[{ required: true, message: t('Please enter a CIDR') }]}
                        tooltip={t('Must be within the VPC\'s CIDR range')}
                    >
                        <Input placeholder={t('E.g. 10.0.1.0/24')} />
                    </Form.Item>

                    <Form.Item name="gateway" label={t('Gateway')}>
                        <Input placeholder={t('E.g. 10.0.1.1')} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MyVpc;
